import { MutableRefObject, UIEvent, useCallback, useEffect, useMemo, useRef } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import debounce from "lodash.debounce";
import {
    CONTAINER_WIDTH_ON_SCROLL,
    EXPAND_SCROLL_THRESHOLD_PX,
    MIN_OVERFLOW_AFTER_SHRINK_PX,
    SHRINK_SCROLL_THRESHOLD_PX,
    TABLE_FIRST_COLUMN_WIDTH,
} from "src/components-mui/base/table-scrollable/constants";
import { useTableScrollableStore } from "src/components-mui/base/table-scrollable/store/table-scrollable-store";
import { TableFirstColumnWidth } from "src/components-mui/base/table-scrollable/table-scrollable.types";
import { useIsomorphicLayoutEffect } from "src/utils/common-util";

interface IScrollMetrics {
    scrollWidth: number;
    clientWidth: number;
}

interface IResolveFirstColumnRawWidthParams {
    widths: TableFirstColumnWidth;
    isMobile: boolean;
    isTablet: boolean;
    isDesktopSmall: boolean;
}

const resolveFirstColumnRawWidth = ({ widths, isMobile, isTablet, isDesktopSmall }: IResolveFirstColumnRawWidthParams) => {
    if (isMobile) {
        return widths[0];
    }
    if (isTablet) {
        return widths[1];
    }
    if (isDesktopSmall) {
        return widths[2];
    }
    return widths[3];
};

const useFirstColumnWidthPx = (firstColumnWidth?: TableFirstColumnWidth) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktopSmall = useMediaQuery(theme.breakpoints.between("md", "lg"));

    const widths = firstColumnWidth ?? (TABLE_FIRST_COLUMN_WIDTH as TableFirstColumnWidth);
    const raw = resolveFirstColumnRawWidth({ widths, isMobile, isTablet, isDesktopSmall });
    return Number(raw.replace("px", ""));
};

const useWindowResize = (onResize: () => void) => {
    useEffect(() => {
        globalThis.window.addEventListener("resize", onResize);
        return () => globalThis.window.removeEventListener("resize", onResize);
    }, [onResize]);
};

export const useTableScrollObserver = <TData extends object>(
    ref: MutableRefObject<HTMLDivElement | null>,
    columns: ColumnDef<TData, unknown>[],
    firstColumnWidth?: TableFirstColumnWidth
) => {
    const store = useTableScrollableStore();
    const columnWidthPx = useFirstColumnWidthPx(firstColumnWidth);

    const hasResetInitialScrollRef = useRef(false);
    const scrollMetricsRef = useRef<IScrollMetrics | null>(null);
    const scrollFrameRef = useRef<number | null>(null);

    const readMetrics = useCallback((element: HTMLDivElement): IScrollMetrics => {
        scrollMetricsRef.current ??= { scrollWidth: element.scrollWidth, clientWidth: element.clientWidth };
        return scrollMetricsRef.current;
    }, []);

    const invalidateMetrics = useCallback(() => {
        scrollMetricsRef.current = null;
    }, []);

    const updateScrollEndShadow = useCallback(
        (element: HTMLDivElement) => {
            const { scrollWidth, clientWidth } = readMetrics(element);
            store.showScrollEndShadow = clientWidth + element.scrollLeft < scrollWidth;
        },
        [readMetrics, store]
    );

    const updateShrunkWidth = useCallback(
        (element: HTMLDivElement) => {
            const { scrollWidth, clientWidth } = readMetrics(element);
            if (scrollWidth <= clientWidth) {
                return;
            }

            const renderedShrinkDelta =
                store.shrinkFirstColumn && store.firstColumnShrunkWidth > 0 ? columnWidthPx - store.firstColumnShrunkWidth : 0;
            const overflowWidth = scrollWidth - clientWidth + renderedShrinkDelta;
            const shrunkWidth = Math.max(CONTAINER_WIDTH_ON_SCROLL, columnWidthPx - overflowWidth + MIN_OVERFLOW_AFTER_SHRINK_PX);

            store.firstColumnShrunkWidth = Math.min(shrunkWidth, columnWidthPx);
        },
        [columnWidthPx, readMetrics, store]
    );

    const debouncedRemeasure = useMemo(
        () =>
            debounce((element: HTMLDivElement) => {
                invalidateMetrics();
                updateScrollEndShadow(element);
                updateShrunkWidth(element);
            }, 100),
        [updateScrollEndShadow, updateShrunkWidth, invalidateMetrics]
    );

    const onResize = useCallback(() => {
        if (ref.current) {
            debouncedRemeasure(ref.current);
        }
    }, [ref, debouncedRemeasure]);
    useWindowResize(onResize);

    useIsomorphicLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        invalidateMetrics();

        if (!hasResetInitialScrollRef.current) {
            hasResetInitialScrollRef.current = true;
            element.scrollLeft = 0;
        }

        updateScrollEndShadow(element);
        updateShrunkWidth(element);

        const onTransitionEnd = () => debouncedRemeasure(element);
        element.addEventListener("transitionend", onTransitionEnd);

        return () => {
            element.removeEventListener("transitionend", onTransitionEnd);
            debouncedRemeasure.cancel();
        };
    }, [updateScrollEndShadow, updateShrunkWidth, ref, debouncedRemeasure, invalidateMetrics, columns]);

    useEffect(() => {
        return () => {
            if (scrollFrameRef.current !== null) {
                cancelAnimationFrame(scrollFrameRef.current);
            }
        };
    }, []);

    const handleOnScroll = (event: UIEvent<HTMLElement>) => {
        const element = event.target as HTMLDivElement;

        if (scrollFrameRef.current !== null) {
            return;
        }

        scrollFrameRef.current = requestAnimationFrame(() => {
            scrollFrameRef.current = null;

            updateScrollEndShadow(element);

            const { scrollLeft } = element;
            const wasShrunk = store.shrinkFirstColumn;
            const shouldShrink = wasShrunk ? scrollLeft > EXPAND_SCROLL_THRESHOLD_PX : scrollLeft > SHRINK_SCROLL_THRESHOLD_PX;

            if (shouldShrink !== wasShrunk) {
                invalidateMetrics();
            }
            store.shrinkFirstColumn = shouldShrink;
        });
    };

    return { handleOnScroll };
};
